import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { siteConfig } from '../../config/site'
import { popularSearchItems, searchPublicPages, type SearchItem } from '../../data/search'
import { reportSearchEvent, SearchContext, useSiteSearch } from './searchContext'

export function SearchButton({ className = '', onOpen }: { className?: string; onOpen?: () => void }) {
  const open = useSiteSearch()
  return <button className={`site-search-trigger ${className}`} type="button" aria-label="Search Pack Print Mail" aria-haspopup="dialog" aria-keyshortcuts="Control+k Meta+k" onClick={() => { onOpen?.(); open() }}>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 5 5" /></svg><span>Search</span>
  </button>
}

export function SiteSearchProvider({ children }: { children: ReactNode }) {
  const dialog = useRef<HTMLDialogElement>(null)
  const input = useRef<HTMLInputElement>(null)
  const resultList = useRef<HTMLUListElement>(null)
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState(-1)
  const [isOpen, setIsOpen] = useState(false)
  const navigate = useNavigate()
  const hasQuery = query.trim().length > 0
  const results = hasQuery ? searchPublicPages(query) : popularSearchItems
  const open = useCallback(() => {
    if (dialog.current?.open) { input.current?.focus(); return }
    setQuery(''); setSelected(-1); setIsOpen(true)
    dialog.current?.showModal()
    input.current?.focus()
  }, [])
  const close = () => { dialog.current?.close(); setIsOpen(false) }

  useEffect(() => {
    const shortcut = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault(); open()
      }
    }
    document.addEventListener('keydown', shortcut)
    return () => document.removeEventListener('keydown', shortcut)
  }, [open])
  useEffect(() => {
    if (!isOpen || !query.trim()) return
    const timer = setTimeout(() => {
      reportSearchEvent({ type: 'search', term: query.trim(), resultCount: results.length })
      if (!results.length) reportSearchEvent({ type: 'zero_results', term: query.trim() })
    }, 300)
    return () => clearTimeout(timer)
  }, [query, results.length, isOpen])
  useEffect(() => {
    if (selected >= 0) resultList.current?.children[selected]?.scrollIntoView({ block: 'nearest' })
  }, [selected])

  const choose = (item: SearchItem) => {
    reportSearchEvent({ type: 'result_clicked', term: query.trim(), resultId: item.id, url: item.url })
    close(); navigate(item.url); window.scrollTo({ top: 0, behavior: 'instant' })
  }
  return <SearchContext.Provider value={open}>{children}
    <dialog className="site-search-dialog" ref={dialog} aria-labelledby="site-search-title" onClose={() => setIsOpen(false)} onKeyDown={(event) => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close() }
    }} onClick={(event) => {
      if (event.target !== event.currentTarget) return
      const bounds = event.currentTarget.getBoundingClientRect()
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) close()
    }}>
      <div className="site-search-heading"><h2 id="site-search-title">Search Pack Print Mail</h2><button type="button" className="site-search-close" aria-label="Close search" onClick={close}>×</button></div>
      <label className="sr-only" htmlFor="site-search-input">Search services, products and information</label>
      <input ref={input} id="site-search-input" type="search" role="combobox" aria-autocomplete="list" aria-expanded={results.length > 0} aria-controls="site-search-results" aria-activedescendant={selected >= 0 && results[selected] ? `search-option-${results[selected].id}` : undefined} autoComplete="off" spellCheck={false} maxLength={120} placeholder="Search services, products and information..." value={query} onChange={(event) => { setQuery(event.target.value); setSelected(-1) }} onKeyDown={(event) => {
        if (event.nativeEvent.isComposing) return
        if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && results.length) {
          event.preventDefault()
          setSelected((value) => event.key === 'ArrowDown' ? (value + 1) % results.length : value <= 0 ? results.length - 1 : value - 1)
        } else if (event.key === 'Enter' && results.length) { event.preventDefault(); choose(results[selected < 0 ? 0 : selected]) }
      }} />
      <p className="site-search-count" role="status" aria-live="polite">{hasQuery ? `${results.length} matching ${results.length === 1 ? 'result' : 'results'}` : 'Popular Services'}</p>
      <ul className="site-search-results" id="site-search-results" role="listbox" aria-label={hasQuery ? 'Search results' : 'Popular services'} ref={resultList}>
        {results.map((item, index) => <li key={item.id} role="presentation"><a id={`search-option-${item.id}`} href={item.url} role="option" aria-selected={selected === index} onFocus={() => setSelected(index)} onClick={(event) => { if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return; event.preventDefault(); choose(item) }}><strong>{item.title}</strong><span>{item.description}</span><small>{item.category}</small></a></li>)}
      </ul>
      {hasQuery && !results.length && <div className="site-search-empty"><h3>Can’t find what you’re looking for?</h3><p>Contact Pack Print Mail and we’ll help you.</p><div><Link className="service-button service-button--primary" to="/contact-us" onClick={close}>Contact Us</Link><a className="service-button service-button--secondary" href={siteConfig.phoneHref}>Call Us</a></div></div>}
      <p className="site-search-help">Use ↑ ↓ to choose a result, Enter to open, or Escape to close.</p>
    </dialog>
  </SearchContext.Provider>
}
